/**
 * Comprehensive Watermark Service
 * Uses public URLs directly for all file operations
 */

import { PDFDocument, rgb, degrees, StandardFonts } from 'pdf-lib';
import JSZip from 'jszip';
import configService from './configService';

class WatermarkService {
  constructor() {
    this.watermarkFontSize = 14; // Default font size
    this.fontSizeLoaded = false;
    this.fontSizePromise = null;
  }

  /**
   * Fetch watermark font size from configuration
   */
  async fetchWatermarkFontSize() {
    try {
      const response = await configService.getPublicConfig('WATERMARK_FONT_SIZE');
      
      // Check different possible response structures
      let fontSize = 14;
      if (response?.data?.value) {
        fontSize = parseInt(response.data.value);
      } else if (response?.data?.WATERMARK_FONT_SIZE) {
        fontSize = parseInt(response.data.WATERMARK_FONT_SIZE);
      } else if (response?.value) {
        fontSize = parseInt(response.value);
      } else if (response?.WATERMARK_FONT_SIZE) {
        fontSize = parseInt(response.WATERMARK_FONT_SIZE);
      }
      
      this.watermarkFontSize = Math.min(Math.max(fontSize, 8), 72); // Clamp between 8 and 72
      this.fontSizeLoaded = true;
    } catch (error) {
      this.watermarkFontSize = 14;
      this.fontSizeLoaded = true;
    }
  }

  /**
   * Ensure font size is loaded
   */
  async ensureFontSizeLoaded() {
    if (!this.fontSizeLoaded) {
      // Use a promise to ensure we only fetch once even if called multiple times
      if (!this.fontSizePromise) {
        this.fontSizePromise = this.fetchWatermarkFontSize();
      }
      await this.fontSizePromise;
    }
  }

  /**
   * Get current watermark font size
   */
  getFontSize() {
    return this.watermarkFontSize;
  }
  /**
   * Generate watermark info from user data
   */
  generateWatermarkInfo(userInfo, dataroomId = null) {
    return {
      userName: `${userInfo?.first_name || ''} ${userInfo?.last_name || ''}`.trim() || 'User',
      userEmail: userInfo?.email || 'user@example.com',
      timestamp: new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }),
      dataroomId: dataroomId || 'DATAROOM'
    };
  }

  /**
   * Fetch file using public URL directly
   */
  async fetchFileFromPublicUrl(publicUrl, fileType = 'application/octet-stream') {
    try {
      
      // Fetch directly from public URL (S3)
      const fileResponse = await fetch(publicUrl, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit', // Don't send credentials for public S3 URL
        headers: {
          'Accept': fileType
        }
      });
      
      if (!fileResponse.ok) {
        throw new Error(`Failed to fetch file: ${fileResponse.status}`);
      }
      
      const blob = await fileResponse.blob();
      
      return blob;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Add watermark to PDF files
   */
  async watermarkPDF(pdfBlob, watermarkInfo) {
    try {
      
      // Ensure font size is loaded
      await this.ensureFontSizeLoaded();
      
      const arrayBuffer = await pdfBlob.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer, {
        ignoreEncryption: true
      });
      
      // Embed fonts
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      
      const pages = pdfDoc.getPages();
      
      // Format watermark text
      const userName = watermarkInfo.userName || 'Authorized User';
      const userEmail = watermarkInfo.userEmail || 'user@example.com';
      const timestamp = watermarkInfo.timestamp || new Date().toISOString();
      const dataroomId = watermarkInfo.dataroomId || 'DATAROOM';
      
      // Watermark pattern text
      const watermarkText = `${userName} • ${userEmail}`;
      const secondaryText = `${timestamp} • DR: ${dataroomId}`;
      
      pages.forEach((page, pageIndex) => {
        const { width, height } = page.getSize();
        
        // Pattern configuration
        const horizontalSpacing = 200;
        const verticalSpacing = 120;
        const rotation = -45;
        
        // Calculate grid
        const cols = Math.ceil(width / horizontalSpacing) + 2;
        const rows = Math.ceil(height / verticalSpacing) + 2;
        
        // Draw diagonal pattern
        for (let row = -1; row < rows; row++) {
          for (let col = -1; col < cols; col++) {
            const x = col * horizontalSpacing - 50;
            const y = row * verticalSpacing - 30;
            
            // Primary text with configured font size
            page.drawText(watermarkText, {
              x: x,
              y: y,
              size: this.getFontSize(),
              font: boldFont,
              color: rgb(0.3, 0.3, 0.3),
              opacity: 0.25,
              rotate: degrees(rotation)
            });
            
            // Secondary text (75% of primary size)
            page.drawText(secondaryText, {
              x: x,
              y: y - (this.getFontSize() * 1.25),
              size: Math.floor(this.getFontSize() * 0.75),
              font: regularFont,
              color: rgb(0.4, 0.4, 0.4),
              opacity: 0.2,
              rotate: degrees(rotation)
            });
          }
        }
        
        // Add header - using smaller font (60% of configured)
        const headerFooterSize = Math.floor(this.getFontSize() * 0.6);
        const headerText = `Downloaded by: ${userName} at ${timestamp}`;
        page.drawText(headerText, {
          x: 10,
          y: height - 15,
          size: headerFooterSize,
          font: regularFont,
          color: rgb(0.3, 0.3, 0.3),
          opacity: 0.5
        });
        
        // Add footer - using smaller font (60% of configured)
        const footerText = `Page ${pageIndex + 1} • ${userEmail} • Dataroom: ${dataroomId}`;
        page.drawText(footerText, {
          x: 10,
          y: 10,
          size: headerFooterSize,
          font: regularFont,
          color: rgb(0.3, 0.3, 0.3),
          opacity: 0.5
        });
      });
      
      const pdfBytes = await pdfDoc.save();
      return new Blob([pdfBytes], { type: 'application/pdf' });
      
    } catch (error) {
      return pdfBlob; // Return original on error
    }
  }

  /**
   * Add watermark to image files (matching PDF watermark style)
   */
  async watermarkImage(imageBlob, watermarkInfo, fileName) {
    try {
      
      // Ensure font size is loaded
      await this.ensureFontSizeLoaded();
      
      // Get font size here, outside the promise
      const configuredFontSize = this.getFontSize();
      
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Enable CORS for images
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        img.onload = function() {
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Draw original image
          ctx.drawImage(img, 0, 0);
          
          // Format watermark text (matching PDF style)
          const userName = watermarkInfo.userName || 'Authorized User';
          const userEmail = watermarkInfo.userEmail || 'user@example.com';
          const timestamp = watermarkInfo.timestamp || new Date().toISOString();
          const dataroomId = watermarkInfo.dataroomId || 'DATAROOM';
          
          const watermarkText = `${userName} • ${userEmail}`;
          const secondaryText = `${timestamp} • DR: ${dataroomId}`;
          
          // Draw diagonal pattern with low opacity (matching PDF style)
          ctx.save();
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate(-45 * Math.PI / 180);
          
          // Pattern configuration (matching PDF spacing and opacity)
          const horizontalSpacing = 200;
          const verticalSpacing = 120;
          const cols = Math.ceil(canvas.width / horizontalSpacing) + 2;
          const rows = Math.ceil(canvas.height / verticalSpacing) + 2;
          
          // Draw light diagonal pattern
          const fontSize = configuredFontSize;
          const secondaryFontSize = Math.floor(fontSize * 0.75);
          
          for (let row = -1; row < rows; row++) {
            for (let col = -1; col < cols; col++) {
              const x = col * horizontalSpacing - 50;
              const y = row * verticalSpacing - 30;
              
              // Primary text (visible but not intrusive)
              ctx.globalAlpha = 0.25;
              ctx.fillStyle = '#4d4d4d';
              ctx.font = `bold ${fontSize}px Arial`;
              ctx.textAlign = 'center';
              ctx.fillText(watermarkText, x, y);
              
              // Secondary text
              ctx.globalAlpha = 0.2;
              ctx.fillStyle = '#666666';
              ctx.font = `${secondaryFontSize}px Arial`;
              ctx.fillText(secondaryText, x, y + (fontSize * 1.25));
            }
          }
          
          ctx.restore();
          
          // Add header text (visible) - using smaller font (60% of configured)
          ctx.globalAlpha = 0.5;
          ctx.fillStyle = '#4d4d4d';
          ctx.font = `${Math.floor(fontSize * 0.6)}px Arial`;
          ctx.textAlign = 'left';
          const headerText = `Downloaded by: ${userName} at ${timestamp}`;
          ctx.fillText(headerText, 10, 15);
          
          // Add footer text (visible) - using smaller font (60% of configured)
          ctx.textAlign = 'left';
          const footerText = `${userEmail} • Dataroom: ${dataroomId}`;
          ctx.fillText(footerText, 10, canvas.height - 5);
          
          // Convert to blob
          canvas.toBlob((blob) => {
            resolve(blob);
          }, imageBlob.type || 'image/png');
        };
        
        img.onerror = function() {
          resolve(imageBlob); // Return original on error
        };
        
        // Load image from blob
        const url = URL.createObjectURL(imageBlob);
        img.src = url;
      });
      
    } catch (error) {
      return imageBlob; // Return original on error
    }
  }

  /**
   * Add watermark to Word documents (.docx)
   */
  async watermarkWord(fileBlob, watermarkInfo) {
    try {
      
      // Ensure font size is loaded
      await this.ensureFontSizeLoaded();
      
      const zip = new JSZip();
      const zipContent = await zip.loadAsync(fileBlob);
      
      // Generate watermark text (matching PDF/image pattern)
      const userName = watermarkInfo.userName || 'Authorized User';
      const userEmail = watermarkInfo.userEmail || 'user@example.com';
      const timestamp = watermarkInfo.timestamp || new Date().toISOString();
      const dataroomId = watermarkInfo.dataroomId || 'DATAROOM';
      
      const primaryText = `${userName} • ${userEmail}`;
      const secondaryText = `${timestamp} • DR: ${dataroomId}`;
      const headerText = `Downloaded by: ${userName} at ${timestamp}`;
      const footerText = `${userEmail} • Dataroom: ${dataroomId}`;
      
      // Check if document.xml exists
      const documentXml = zipContent.file('word/document.xml');
      if (documentXml) {
        let content = await documentXml.async('string');
        
        // Word uses font size in half-points, so multiply by 2
        const fontSize = this.getFontSize() * 2;
        const secondaryFontSize = Math.floor(fontSize * 0.75);
        const headerFontSize = 16; // Header is smaller (8pt = 16 half-points)
        
        // Insert watermark pattern at the beginning of the document
        const watermarkParagraphs = `
          <!-- Header watermark -->
          <w:p>
            <w:pPr>
              <w:jc w:val="left"/>
              <w:spacing w:after="120"/>
            </w:pPr>
            <w:r>
              <w:rPr>
                <w:sz w:val="${headerFontSize}"/>
                <w:color w:val="7F7F7F"/>
              </w:rPr>
              <w:t>${headerText}</w:t>
            </w:r>
          </w:p>
          
          <!-- Diagonal pattern watermark -->
          <w:p>
            <w:pPr>
              <w:jc w:val="center"/>
              <w:spacing w:after="60"/>
            </w:pPr>
            <w:r>
              <w:rPr>
                <w:sz w:val="${fontSize}"/>
                <w:color w:val="BFBFBF"/>
                <w:i/>
              </w:rPr>
              <w:t>${primaryText}</w:t>
            </w:r>
          </w:p>
          
          <w:p>
            <w:pPr>
              <w:jc w:val="center"/>
              <w:spacing w:after="60"/>
            </w:pPr>
            <w:r>
              <w:rPr>
                <w:sz w:val="${secondaryFontSize}"/>
                <w:color w:val="CCCCCC"/>
              </w:rPr>
              <w:t>${secondaryText}</w:t>
            </w:r>
          </w:p>
          
          <w:p>
            <w:pPr>
              <w:jc w:val="center"/>
              <w:spacing w:after="240"/>
            </w:pPr>
            <w:r>
              <w:rPr>
                <w:sz w:val="${fontSize}"/>
                <w:color w:val="BFBFBF"/>
                <w:i/>
              </w:rPr>
              <w:t>${primaryText}</w:t>
            </w:r>
          </w:p>`;
        
        // Find the first paragraph and insert watermark before it
        content = content.replace(/<w:body[^>]*>/, `<w:body>${watermarkParagraphs}`);
        
        // Update the document
        zipContent.file('word/document.xml', content);
      }
      
      // Generate new blob
      const newBlob = await zipContent.generateAsync({type: 'blob'});
      return newBlob;
      
    } catch (error) {
      return fileBlob; // Return original on error
    }
  }

  /**
   * Add watermark to Excel documents (.xlsx)
   */
  async watermarkExcel(fileBlob, watermarkInfo) {
    try {
      
      // Ensure font size is loaded
      await this.ensureFontSizeLoaded();
      
      const zip = new JSZip();
      const zipContent = await zip.loadAsync(fileBlob);
      
      // Generate watermark text (matching PDF/image pattern)
      const userName = watermarkInfo.userName || 'Authorized User';
      const userEmail = watermarkInfo.userEmail || 'user@example.com';
      const timestamp = watermarkInfo.timestamp || new Date().toISOString();
      const dataroomId = watermarkInfo.dataroomId || 'DATAROOM';
      
      const primaryText = `${userName} • ${userEmail}`;
      const secondaryText = `${timestamp} • DR: ${dataroomId}`;
      const headerText = `Downloaded by: ${userName} at ${timestamp}`;
      const footerText = `${userEmail} • Dataroom: ${dataroomId}`;
      
      // Check if the main worksheet exists
      const worksheetFile = zipContent.file('xl/worksheets/sheet1.xml');
      if (worksheetFile) {
        let content = await worksheetFile.async('string');
        
        // Add watermark pattern in multiple cells (matching PDF/image pattern)
        const watermarkCells = `
          <row r="1">
            <c r="A1" t="inlineStr">
              <is>
                <t>${headerText}</t>
              </is>
            </c>
          </row>
          <row r="2">
            <c r="A2" t="inlineStr">
              <is>
                <t>${primaryText}</t>
              </is>
            </c>
          </row>
          <row r="3">
            <c r="A3" t="inlineStr">
              <is>
                <t>${secondaryText}</t>
              </is>
            </c>
          </row>
          <row r="4">
            <c r="A4" t="inlineStr">
              <is>
                <t>${primaryText}</t>
              </is>
            </c>
          </row>
          <row r="5">
            <c r="A5" t="inlineStr">
              <is>
                <t>${footerText}</t>
              </is>
            </c>
          </row>`;
        
        // Find sheetData and insert watermark pattern
        content = content.replace(/<sheetData[^>]*>/, `<sheetData>${watermarkCells}`);
        
        // Update the worksheet
        zipContent.file('xl/worksheets/sheet1.xml', content);
      }
      
      // Generate new blob
      const newBlob = await zipContent.generateAsync({type: 'blob'});
      return newBlob;
      
    } catch (error) {
      return fileBlob; // Return original on error
    }
  }

  /**
   * Add watermark to PowerPoint documents (.pptx)
   */
  async watermarkPowerPoint(fileBlob, watermarkInfo) {
    try {
      
      // Ensure font size is loaded
      await this.ensureFontSizeLoaded();
      
      const zip = new JSZip();
      const zipContent = await zip.loadAsync(fileBlob);
      
      // Generate watermark text (matching PDF/image pattern)
      const userName = watermarkInfo.userName || 'Authorized User';
      const userEmail = watermarkInfo.userEmail || 'user@example.com';
      const timestamp = watermarkInfo.timestamp || new Date().toISOString();
      const dataroomId = watermarkInfo.dataroomId || 'DATAROOM';
      
      const primaryText = `${userName} • ${userEmail}`;
      const secondaryText = `${timestamp} • DR: ${dataroomId}`;
      const headerText = `Downloaded by: ${userName} at ${timestamp}`;
      
      // Get all slide files
      const slideFiles = Object.keys(zipContent.files).filter(name => 
        name.startsWith('ppt/slides/slide') && name.endsWith('.xml')
      );
      
      // Add watermark pattern to each slide
      for (const slideFile of slideFiles) {
        const slide = zipContent.file(slideFile);
        if (slide) {
          let content = await slide.async('string');
          
          // PowerPoint uses font size in 100ths of a point (100 = 1pt)
          const fontSize = this.getFontSize() * 100;
          const secondaryFontSize = Math.floor(fontSize * 0.75);
          const headerFontSize = 800; // Header is smaller (8pt = 800)
          
          // Add multiple watermark text boxes to create pattern effect (matching PDF/image)
          const watermarkShapes = `
            <!-- Header watermark -->
            <p:sp>
              <p:nvSpPr>
                <p:cNvPr id="997" name="HeaderWatermark"/>
                <p:cNvSpPr/>
                <p:nvPr/>
              </p:nvSpPr>
              <p:spPr>
                <a:xfrm>
                  <a:off x="914400" y="365760"/>
                  <a:ext cx="7315200" cy="365760"/>
                </a:xfrm>
                <a:prstGeom prst="rect"/>
                <a:noFill/>
                <a:ln w="0"/>
              </p:spPr>
              <p:txBody>
                <a:bodyPr/>
                <a:p>
                  <a:r>
                    <a:rPr sz="${headerFontSize}" b="0">
                      <a:solidFill>
                        <a:srgbClr val="7F7F7F"/>
                      </a:solidFill>
                    </a:rPr>
                    <a:t>${headerText}</a:t>
                  </a:r>
                </a:p>
              </p:txBody>
            </p:sp>
            
            <!-- Primary diagonal watermark -->
            <p:sp>
              <p:nvSpPr>
                <p:cNvPr id="998" name="PrimaryWatermark"/>
                <p:cNvSpPr/>
                <p:nvPr/>
              </p:nvSpPr>
              <p:spPr>
                <a:xfrm>
                  <a:off x="2743200" y="2743200"/>
                  <a:ext cx="4572000" cy="914400"/>
                </a:xfrm>
                <a:prstGeom prst="rect"/>
                <a:noFill/>
                <a:ln w="0"/>
              </p:spPr>
              <p:txBody>
                <a:bodyPr/>
                <a:p>
                  <a:pPr>
                    <a:defRPr sz="${fontSize}" i="1">
                      <a:solidFill>
                        <a:srgbClr val="BFBFBF"/>
                      </a:solidFill>
                    </a:defRPr>
                  </a:pPr>
                  <a:r>
                    <a:rPr sz="${fontSize}" i="1">
                      <a:solidFill>
                        <a:srgbClr val="BFBFBF"/>
                      </a:solidFill>
                    </a:rPr>
                    <a:t>${primaryText}</a:t>
                  </a:r>
                </a:p>
              </p:txBody>
            </p:sp>
            
            <!-- Secondary diagonal watermark -->
            <p:sp>
              <p:nvSpPr>
                <p:cNvPr id="999" name="SecondaryWatermark"/>
                <p:cNvSpPr/>
                <p:nvPr/>
              </p:nvSpPr>
              <p:spPr>
                <a:xfrm>
                  <a:off x="2743200" y="3657600"/>
                  <a:ext cx="4572000" cy="914400"/>
                </a:xfrm>
                <a:prstGeom prst="rect"/>
                <a:noFill/>
                <a:ln w="0"/>
              </p:spPr>
              <p:txBody>
                <a:bodyPr/>
                <a:p>
                  <a:r>
                    <a:rPr sz="${secondaryFontSize}" b="0">
                      <a:solidFill>
                        <a:srgbClr val="CCCCCC"/>
                      </a:solidFill>
                    </a:rPr>
                    <a:t>${secondaryText}</a:t>
                  </a:r>
                </a:p>
              </p:txBody>
            </p:sp>`;
          
          // Find spTree and add watermark shapes
          content = content.replace(/<\/p:spTree>/, `${watermarkShapes}</p:spTree>`);
          
          // Update the slide
          zipContent.file(slideFile, content);
        }
      }
      
      // Generate new blob
      const newBlob = await zipContent.generateAsync({type: 'blob'});
      return newBlob;
      
    } catch (error) {
      return fileBlob; // Return original on error
    }
  }

  /**
   * Apply watermark based on file type
   */
  async applyWatermark(fileBlob, watermarkInfo, mimeType, fileName, watermarkEnabled = true) {
    try {
      // If watermark is disabled, return original file
      if (!watermarkEnabled) {
        return fileBlob;
      }
      
      
      // PDF files
      if (mimeType === 'application/pdf' || fileName?.toLowerCase().endsWith('.pdf')) {
        return await this.watermarkPDF(fileBlob, watermarkInfo);
      }
      
      // Image files
      if (mimeType?.startsWith('image/') || 
          fileName?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i)) {
        return await this.watermarkImage(fileBlob, watermarkInfo, fileName);
      }
      
      // Word documents
      if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
          fileName?.toLowerCase().endsWith('.docx')) {
        return await this.watermarkWord(fileBlob, watermarkInfo);
      }
      
      // Excel documents
      if (mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
          fileName?.toLowerCase().endsWith('.xlsx')) {
        return await this.watermarkExcel(fileBlob, watermarkInfo);
      }
      
      // PowerPoint documents
      if (mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' || 
          fileName?.toLowerCase().endsWith('.pptx')) {
        return await this.watermarkPowerPoint(fileBlob, watermarkInfo);
      }
      
      // Other files - return original (no watermark support)
      return fileBlob;
      
    } catch (error) {
      return fileBlob; // Return original on error
    }
  }

  /**
   * Process file for preview with watermark using public URL
   */
  async processFileForPreview(file, userInfo, dataroomUuid = null, watermarkEnabled = true) {
    try {
      const publicUrl = file.public_url;
      const mimeType = file.mimeType || file.mime_type || 'application/octet-stream';
      const fileName = file.name || file.display_name || 'file';
      
      if (!publicUrl) {
        throw new Error('No public URL available for file');
      }
      
      // Generate watermark info
      const watermarkInfo = this.generateWatermarkInfo(userInfo, dataroomUuid);
      
      // For PDFs, return info for WatermarkedPDFViewer component
      if (mimeType === 'application/pdf' || fileName?.toLowerCase().endsWith('.pdf')) {
        return {
          url: publicUrl,
          isWatermarked: true,
          watermarkInfo: watermarkInfo,
          needsPDFViewer: true, // Flag to use WatermarkedPDFViewer
          fileName: fileName
        };
      }
      
      // For images, fetch and apply watermark if enabled
      if (mimeType?.startsWith('image/') || fileName?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i)) {
        if (watermarkEnabled) {
          const fileBlob = await this.fetchFileFromPublicUrl(publicUrl, mimeType);
          const watermarkedBlob = await this.watermarkImage(fileBlob, watermarkInfo, fileName);
          const previewUrl = URL.createObjectURL(watermarkedBlob);
          
          return {
            url: previewUrl,
            blob: watermarkedBlob,
            isWatermarked: true,
            watermarkInfo: watermarkInfo
          };
        } else {
          // Return original image URL if watermark disabled
          return {
            url: publicUrl,
            isWatermarked: false,
            watermarkInfo: null
          };
        }
      }
      
      // For Office documents, return basic info (handled by direct download)
      if ((mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
           fileName?.toLowerCase().endsWith('.docx')) ||
          (mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
           fileName?.toLowerCase().endsWith('.xlsx')) ||
          (mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' || 
           fileName?.toLowerCase().endsWith('.pptx'))) {
        
        return {
          url: publicUrl,
          isWatermarked: false,
          watermarkInfo: null // Office documents handled in download flow
        };
      }
      
      // For other files, return public URL directly
      return {
        url: publicUrl,
        isWatermarked: false,
        watermarkInfo: null
      };
      
    } catch (error) {
      throw error;
    }
  }

  /**
   * Process file for download with watermark using public URL
   */
  async processFileForDownload(file, userInfo, dataroomUuid = null, watermarkEnabled = true) {
    try {
      const publicUrl = file.public_url;
      const mimeType = file.mimeType || file.mime_type || 'application/octet-stream';
      const fileName = file.name || file.display_name || 'file';
      
      if (!publicUrl) {
        throw new Error('No public URL available for file');
      }
      
      // Fetch file from public URL
      const fileBlob = await this.fetchFileFromPublicUrl(publicUrl, mimeType);
      
      // Generate watermark info
      const watermarkInfo = this.generateWatermarkInfo(userInfo, dataroomUuid);
      
      // Apply watermark
      const watermarkedBlob = await this.applyWatermark(fileBlob, watermarkInfo, mimeType, fileName, watermarkEnabled);
      
      return {
        blob: watermarkedBlob,
        fileName: fileName,
        isWatermarked: watermarkEnabled,
        watermarkInfo: watermarkEnabled ? watermarkInfo : null
      };
      
    } catch (error) {
      throw error;
    }
  }
}

// Export singleton instance
const watermarkService = new WatermarkService();
export default watermarkService;